import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Sun, Smartphone, Shield } from "lucide-react";

export default function HeroSimple() {
  const [theme, setTheme] = React.useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  const [mounted, setMounted] = useState(false);

  React.useEffect(() => {
    const handleStorage = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('storage', handleStorage);
    setMounted(true);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <section className={`flex-1 flex flex-col justify-center items-center text-center px-4 py-20 md:py-32 relative ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className="space-y-6 max-w-4xl mx-auto relative z-10">
        {/* Badges */}
        <div className={`flex flex-wrap justify-center gap-2 mb-6 transition-all duration-1000 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <span className={`inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium transition-all duration-300 hover:scale-105 ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700 text-gray-300 hover:border-orange-500/50' 
              : 'bg-gray-100 border-gray-200 text-gray-700 hover:border-orange-300'
          }`}>
            Congressional App Challenge Winner 2025
          </span>
        </div>

        {/* Main Heading */}
        <h1 className={`text-4xl md:text-6xl font-bold mb-4 transition-all duration-1000 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`} style={{ transitionDelay: '100ms' }}>
          <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
            The smart way to protect your skin
          </span>
        </h1>
        
        <p className={`text-lg md:text-xl mb-4 max-w-2xl mx-auto leading-relaxed transition-all duration-1000 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        } ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} style={{ transitionDelay: '200ms' }}>
          AI-powered UV monitoring and skin health tracking.
        </p>
        <p className={`text-lg md:text-xl mb-8 max-w-2xl mx-auto leading-relaxed transition-all duration-1000 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        } ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} style={{ transitionDelay: '300ms' }}>
          Real-time alerts, personalized recommendations, peace of mind.
        </p>

        {/* CTA Buttons */}
        <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center mb-8 transition-all duration-1000 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`} style={{ transitionDelay: '400ms' }}>
          <a 
            href="https://apps.apple.com/us/app/sunscreened/id6756286578" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block"
          >
            <Button size="lg" className={`gap-2 ${
              theme === 'dark'
                ? 'bg-gray-800 text-white border-gray-700 hover:bg-gray-700'
                : 'bg-gray-900 text-white border-gray-800 hover:bg-gray-800'
            }`}>
              Get Started
            </Button>
          </a>
          <Button 
            size="lg" 
            variant="outline"
            className={`gap-2 ${
              theme === 'dark'
                ? 'border-gray-700 text-white hover:bg-gray-800'
                : 'border-gray-200 text-gray-900 hover:bg-gray-50'
            }`}
          >
            Learn More
          </Button>
          <a 
            href="https://github.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block"
          >
            <Button 
              size="lg" 
              variant="outline"
              className={`gap-2 ${
                theme === 'dark'
                  ? 'border-gray-700 text-white hover:bg-gray-800'
                  : 'border-gray-200 text-gray-900 hover:bg-gray-50'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"></path>
                <path d="M9 18c-4.51 2-5-2-7-2"></path>
              </svg>
              GitHub
            </Button>
          </a>
        </div>

        {/* Stats */}
        <div className={`flex flex-wrap justify-center gap-4 transition-all duration-1000 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`} style={{ transitionDelay: '500ms' }}>
          <div className="w-full max-w-4xl mx-auto pt-12">
            <div className="flex justify-around items-start text-center">
              {[
                { value: "Real-time", label: "UV Tracking" },
                { value: "AI-Powered", label: "Skin Analysis" },
                { value: "Zero", label: "Data Privacy Concerns" }
              ].map((stat, index) => (
                <div key={index} className="px-2 transition-all duration-300 hover:scale-105">
                  <p className={`text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-1 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {stat.value}
                  </p>
                  <p className={`text-xs sm:text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

